import _ from 'lodash';

import {
  ARRAY_FUNCTIONS,
  ARRAY_FUNCTIONS_WITH_ITERATORS,
  EXPRESSION_TYPES,
  LEX_TOKEN_TYPES,
} from 'constants/parser';
import { getTokenPriority, normalizeAstKeys } from 'utils/expressions';
import { jsonStringify, parseJSONObject } from 'utils/json';

class UserFriendlyExpressionBuilder {
  static buildOperator = (
    node,
    { parent, isSecondOperand, isNewExpressionsParsingLogicEnabled },
  ) => {
    const { value: operatorValue, operands } = node;

    const [operand1, operand2] = operands;

    const operand1Built = this.build(operand1, false, {
      parent: node,
      isNewExpressionsParsingLogicEnabled,
    });
    const operand2Built = this.build(operand2, false, {
      parent: node,
      isSecondOperand: true,
      isNewExpressionsParsingLogicEnabled,
    });

    const parentPriority = getTokenPriority(parent?.value);
    const childPriority = getTokenPriority(node?.value);

    const isWeakPosition =
      isSecondOperand &&
      ((parent?.value === '/' && node?.value === '*') ||
        (parent?.value === '-' && node?.value === '+'));

    if (isNewExpressionsParsingLogicEnabled && (childPriority < parentPriority || isWeakPosition)) {
      return `(${operand1Built} ${operatorValue} ${operand2Built})`;
    }

    return `${operand1Built} ${operatorValue} ${operand2Built}`;
  };

  static buildArrayFunction = (node, { isNewExpressionsParsingLogicEnabled }) => {
    const { value: functionValue, operands } = node;

    const isFunctionWithIterator = ARRAY_FUNCTIONS_WITH_ITERATORS.includes(functionValue);
    const iteratorIndex = 1;

    const builtOperands = operands.reduce((acc, operand, index) => {
      if (isFunctionWithIterator && index === iteratorIndex) {
        return acc;
      }

      acc.push(
        this.build(operand, false, {
          parent: node,
          isNewExpressionsParsingLogicEnabled,
        }),
      );

      return acc;
    }, []);

    return `${functionValue}(${builtOperands.join(', ')})`;
  };

  static buildFunction = (node, { isNewExpressionsParsingLogicEnabled }) => {
    const { value: functionValue, operands } = node;

    const builtOperands = operands.map(operand =>
      this.build(operand, false, { parent: node, isNewExpressionsParsingLogicEnabled }),
    );

    return `${functionValue}(${builtOperands.join(', ')})`;
  };

  static buildValue = (
    { value, typeOfValue, type, operands },
    { isNewExpressionsParsingLogicEnabled },
  ) => {
    const lowerCasedTypeOfValue = typeOfValue?.toLowerCase();
    const lowerCasedType = type?.toLowerCase();

    const isStringOrEnumValue =
      (!Object.values(EXPRESSION_TYPES).includes(lowerCasedTypeOfValue) ||
        lowerCasedTypeOfValue === EXPRESSION_TYPES.STRING) &&
      lowerCasedType !== LEX_TOKEN_TYPES.field &&
      lowerCasedTypeOfValue !== LEX_TOKEN_TYPES.bool;

    if (operands?.length) {
      const functionExpression = UserFriendlyExpressionBuilder.build(
        jsonStringify(operands[0]),
        true,
        { isNewExpressionsParsingLogicEnabled },
      );

      const extractedProp = _.split(value, '.').at(-1);

      return `${functionExpression}.${extractedProp}`;
    }

    return isStringOrEnumValue ? `'${value}'` : value;
  };

  /**
   * A caller passing `isRootExpression: false` owns key-casing normalization of the node it hands over
   * (see `normalizeAstKeys`) — normalizing on every recursive step would re-walk each subtree once per level.
   */
  static build = (
    schema,
    isRootExpression = false,
    { parent = null, isSecondOperand = false, isNewExpressionsParsingLogicEnabled = false } = {},
  ) => {
    const expression = isRootExpression ? normalizeAstKeys(parseJSONObject(schema)) : schema;

    if (_.isEmpty(expression)) {
      return '';
    }

    if (
      expression.type.toLowerCase() === LEX_TOKEN_TYPES.function &&
      ARRAY_FUNCTIONS.includes(expression.value)
    ) {
      return this.buildArrayFunction(expression, { parent, isNewExpressionsParsingLogicEnabled });
    }

    if (expression.type.toLowerCase() === LEX_TOKEN_TYPES.function) {
      return this.buildFunction(expression, { parent, isNewExpressionsParsingLogicEnabled });
    }

    if (expression.type.toLowerCase() === LEX_TOKEN_TYPES.operator) {
      return this.buildOperator(expression, {
        parent,
        isSecondOperand,
        isNewExpressionsParsingLogicEnabled,
      });
    }

    if ([LEX_TOKEN_TYPES.value, LEX_TOKEN_TYPES.field].includes(expression.type.toLowerCase())) {
      return this.buildValue(expression, { parent, isNewExpressionsParsingLogicEnabled });
    }
  };
}

export default UserFriendlyExpressionBuilder;
